<template>
    <div>
        <h2>Grunddaten</h2>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Position"
                :options-loader="loadCostUnitCodeOptions"
                property="costUnitCode"
                :required="true"
                type="autocomplete"
                @change="handleCostUnitCodeChange"/>
            <csp-desktop-info-card-element
                :auto-select-single-option="true"
                class="col-3"
                :dependencies="['costUnitCode']"
                :form-context="formContext"
                label="Quelle"
                :options-loader="loadSourceOptions"
                property="source"
                :required="true"
                type="autocomplete"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Beziffert am"
                property="dateReceipt"
                :required="true"
                type="datepicker"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                :is-loading-from-parent="isLoadingHideSettings > 0"
                label="Nicht anzeigen für"
                :mandatory-hide-for-group="mandatoryHideGroups"
                :options="hideForGroupOptions"
                property="hide"
                :record-id="recordId"
                :required="true"
                type="hideForGroup"/>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                v-if="showCustomFields"
                class="col-3"
                :form-context="formContext"
                label="Beschreibung Position"
                property="costUnitText"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Beleg Nr."
                property="receipt"
                type="text"/>
            <csp-desktop-info-card-element
                :auto-select-single-option="true"
                class="col-3"
                :default-value="formContext.data.defaultInsuranceFigure"
                :dependencies="['costUnitCode']"
                :form-context="formContext"
                label="Bezifferungsart"
                :options-loader="loadInsuranceFigureOptions"
                property="insuranceFigure"
                :required="true"
                type="autocomplete"/>
            <csp-desktop-info-card-element
                v-if="showAssessmentField"
                class="col-3"
                :form-context="formContext"
                label="GA erstellt am"
                property="dateAssessmentCreated"
                type="datepicker"/>
        </b-row>
        <b-row v-if="showRentVehicleFields">
            <csp-desktop-info-card-element
                class="col-3"
                :dependencies="['costUnitCode']"
                :form-context="formContext"
                label="Schwacke Kl. d. Mietwagens"
                :options-loader="loadVehicleRentClassOptions"
                property="carRentalVehicleRentClass"
                type="autocomplete"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Anzahl Miettage lt. Rechnung"
                property="carRentalRefundedRentDays"
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Anzahl Miettage lt. Versicherung"
                property="carRentDaysInsurance"
                type="text"/>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                class="col-9"
                :form-context="formContext"
                label="Bemerkung"
                property="paymentComment"
                type="textarea"/>
        </b-row>
        <hr>
        <csp-record-detail-cost-unit-workshop-invoice-positions
            v-if="formContext.permissions.canIndexSubtotals === true"
            :form-context="formContext"/>
        <h2>Betrag</h2>
        <b-row>
            <csp-desktop-info-card-element
                v-show="showCopaymentField"
                class="col-3"
                :form-context="formContext"
                label="Selbstbeteiligung"
                property="amountCopayment"
                type="currency"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Automatische Berechnung"
                property="autoCalc"
                type="radio"/>
            <b-col cols="3"/>
            <csp-desktop-info-card-element
                class="col-3"
                :disabled="true"
                :form-context="formContext"
                label="Akzeptierte Minderung (netto)"
                property="amountAdjustmentNet"
                type="currency"/>
            <csp-desktop-info-card-element
                class="col-3"
                :disabled="true"
                :form-context="formContext"
                label="Akzeptierte Minderung (brutto)"
                property="amountAdjustmentGross"
                type="currency"/>
        </b-row>
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Nettobetrag"
                property="amountNet"
                :required="true"
                type="currency"
                @change="recalculateFromNet"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Bruttobetrag"
                property="amount"
                :required="true"
                type="currency"
                @change="recalculateFromGross"/>
            <csp-desktop-info-card-element
                :auto-select-single-option="true"
                class="col-3"
                :dependencies="['costUnitCode']"
                :form-context="formContext"
                label="USt."
                :options-loader="loadVatRateOptions"
                property="vatRate"
                :required="true"
                type="autocomplete"
                @change="recalculateFromVatPercent"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="USt.-Betrag"
                property="amountVat"
                :required="true"
                type="currency"
                @change="recalculateAmountVat"/>
        </b-row>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import '@desktop/scss/components/record/document-upload-modal.scss';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import CostUnitCodeEnum from '@js/enums/damage/CostUnitCodeEnum';
    import CostUnitSourceEnum from '@js/enums/damage/CostUnitSourceEnum';
    import CostUnitWorkshopInvoicePositions from
    '@desktop/components/record/cost-unit/RecordCostUnitWorkshopInvoicePositions';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'CostUnitBasedata',
        components: {
            'csp-record-detail-cost-unit-workshop-invoice-positions': CostUnitWorkshopInvoicePositions,
        },
        mixins: [FormDataMixin],
        props: {
            hideForGroupOptions: {
                type: Array,
                default: null,
            },
            recordId: {
                type: Number,
                required: true,
            },
        },
        data() {
            return {
                isLoadingHideSettings: 0,
                lastCostUnitCode: null,
                // Role groups that are forced-hidden for the currently selected cost unit code.
                mandatoryHideGroups: [],
                // The user's own hide selection at the time forced groups were last applied.
                // Used to compute which forced groups were net-new so only those are removed on code change.
                userHideValues: null,
            }
        },
        computed: {
            costUnitCode() {
                return this.formContext.form.getProperty('costUnitCode');
            },
            source() {
                return this.formContext.form.getProperty('source');
            },
            amountNet() {
                return this.formContext.form.getProperty('amountNet');
            },
            amountGross() {
                return this.formContext.form.getProperty('amount');
            },
            amountVat() {
                return this.formContext.form.getProperty('amountVat');
            },
            vatRate() {
                return this.formContext.form.getProperty('vatRate');
            },
            vatRatePercent() {
                if (!this.vatRate) {
                    return 0;
                }

                const mapping = this.formContext.complexFields.vatRate.contextData
                return mapping[this.vatRate] / 100 + 1;
            },
            showAssessmentField() {
                return this.costUnitCode === CostUnitCodeEnum.REPAIR && this.source === CostUnitSourceEnum.ASSESSMENT;
            },
            showCopaymentField() {
                return this.costUnitCode === CostUnitCodeEnum.REPAIR && this.recordCanHaveCostUnitCopayment();
            },
            showCustomFields() {
                return this.costUnitCode === CostUnitCodeEnum.CUSTOM;
            },
            showRentVehicleFields() {
                return this.costUnitCode === CostUnitCodeEnum.RENTVEHICLE;
            },
        },
        watch: {
            // When the form data loads (e.g. when opening the edit modal for an existing cost unit),
            // the costUnitCode may already be a fee code. Apply mandatory settings on initial load.
            costUnitCode(newVal) {
                this.isLoadingHideSettings++;
                this.selectedCostUnitCode = newVal;
                if (newVal) {
                    this.loadMandatoryHideForCostUnit(newVal);
                } else {
                    this.updateForcedHideGroups([]);
                    this.isLoadingHideSettings--;
                }
            },
        },
        methods: {
            recordCanHaveCostUnitCopayment() {
                return this.formContext.data.recordCanHaveCostUnitCopayment;
            },
            handleCostUnitCodeChange() {
                // For common, we expect no receipt, so we set it to 'ohne'.
                // If the last cost unit code was common, we reset the receipt field (to remove 'ohne' again).
                if (this.costUnitCode === CostUnitCodeEnum.COMMON) {
                    this.formContext.form.setProperty('receipt', 'ohne');
                } else if (this.lastCostUnitCode === CostUnitCodeEnum.COMMON) {
                    this.formContext.form.setProperty('receipt', '');
                }

                if (this.showCopaymentField
                    && this.formContext.form.getProperty('isEntityNew')
                    && this.formContext.data.defaultAmountCopayment) {
                    this.formContext.form.setProperty('amountCopayment', this.formContext.data.defaultAmountCopayment);
                }

                // set new code as last code for next change
                this.lastCostUnitCode = this.costUnitCode;
            },
            // Fetches mandatory-hidden role groups for the given cost unit code and applies them.
            loadMandatoryHideForCostUnit(costUnitCodeId) {
                recordRepository.hideForGroupMandatoryForCostUnit(
                    this.recordId,
                    {costUnitCodeId}
                ).then((response) => {
                    // Discard the response if the code unit code has changed since the request was launched.
                    if (this.selectedCostUnitCode !== costUnitCodeId) {
                        return;
                    }
                    this.updateForcedHideGroups(response.data.attributes.mandatoryHideGroups);
                }).finally(() => {
                    this.isLoadingHideSettings--;
                });
            },
            // Removes previously forced groups, then applies the new forced groups on top.
            // Passing an empty array clears any previously forced groups.
            updateForcedHideGroups(groups) {
                // Compute which of the previously forced groups were net-new additions (not already
                // checked by the user). Only those should be removed — groups the user had checked
                // independently must survive even if they were also in the previous forced set.
                const current = this.formContext.form.getProperty('hide') ?? [];
                const netForced = this.userHideValues !== null
                    ? this.mandatoryHideGroups.filter((g) => !this.userHideValues.includes(g))
                    : this.mandatoryHideGroups;
                const withoutNetForced = current.filter((g) => !netForced.includes(g));
                this.userHideValues = groups.length > 0 ? [...withoutNetForced] : null;
                this.mandatoryHideGroups = groups;
                const merged = [...new Set([...withoutNetForced, ...groups])];
                this.formContext.form.setProperty('hide', merged);
            },
            recalculateFromNet() {
                if (!this.shouldRecalculateAmounts()) {
                    return;
                }

                const ratePercent = this.vatRatePercent
                const net = this.amountNet;
                let gross = this.amountGross;

                if (ratePercent > 0) {
                    gross = Math.round(net * ratePercent);
                } else {
                    gross = net;
                }

                this.formContext.form.setProperty('amount', gross);
                this.formContext.form.setProperty('amountVat', gross - net);
            },
            recalculateFromGross() {
                if (!this.shouldRecalculateAmounts()) {
                    return;
                }

                const gross = this.amountGross;
                const ratePercent = this.vatRatePercent
                let net = this.amountNet;
                if (ratePercent > 0) {
                    net = Math.round(gross / ratePercent);
                } else {
                    net = gross;
                }

                this.formContext.form.setProperty('amountNet', net);
                this.formContext.form.setProperty('amountVat', gross - net);
            },
            recalculateFromVatPercent() {
                if (this.amountGross.valueOf()) {
                    this.recalculateFromGross();
                    return;
                }

                this.recalculateFromNet();
            },
            recalculateAmountVat() {
                if (!this.shouldRecalculateAmounts()) {
                    return;
                }

                // amounts in cent
                const gross = this.amountGross;
                const net = this.amountNet;
                let vat = this.amountVat;

                const vatCalc = gross - net;
                const vatDiff = Math.abs(vatCalc - vat);

                if (
                    vatDiff === 0 ||
                    confirm(
                        'Der eingegebene USt-Betrag unterscheidet ' +
                            'sich um ' + this.formatAmountForDisplay(vatDiff) + '€ vom ' +
                            'errechneten USt-Betrag. Möchten Sie den ' +
                            'eingegebenen Betrag durch den errechneten ' +
                            'Betrag ' + this.formatAmountForDisplay(vatCalc) + '€ ersetzen? '
                    )
                ) {
                    vat = vatCalc;
                }

                this.formContext.form.setProperty('amountVat', vat);
            },
            formatAmountForDisplay(centAmount) {
                // format the cent amount as a euro amount, using the decimal separator of the active locale
                return (centAmount / 100).toFixed(2).toLocaleString();
            },
            shouldRecalculateAmounts() {
                const autoCalc = this.formContext.form.getProperty('autoCalc');
                return autoCalc && !!this.vatRate
            },
            loadCostUnitCodeOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'vehicleDamageRecordCostUnitCodes',
                    {vehicleDamageRecordId: this.recordId}
                );
            },
            loadSourceOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'vehicleDamageRecordCostUnitSources',
                    {vehicleDamageRecordCostUnitCodeId: this.formContext.form.getProperty('costUnitCode')}
                );
            },
            loadVehicleRentClassOptions() {
                return recordRepository.loadLegacySelectOptions('rentClass');
            },
            loadInsuranceFigureOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'insuranceFigure',
                    {vehicleDamageRecordCostUnitCodeId: this.formContext.form.getProperty('costUnitCode')}
                );
            },
            loadVatRateOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'vehicleDamageRecordCostUnitVatRates',
                    {
                        vehicleDamageRecordId: this.recordId,
                        vehicleDamageRecordCostUnitCodeId: this.formContext.form.getProperty('costUnitCode'),
                    }
                );
            },
        },
    }
</script>
